import type { LensAggregate, LensCardType, LensCardView, LensDatasetColumn } from '~/types/leLens';

import { type ReadyField, aggregateFieldInputs, applyAggregateFields } from './datasetFields';
import { LENS_RATIO_PARTS, OTHER_SERIES, isDateLikeText, isMeasureColumn, isNumeric } from './lensChart';
import { bucketOf, parseGrain } from './lensGrain';
import { namedTableRatios, tableMeasureInputs } from './namedTableRatios';

export type Shaped = { rows: Array<Record<string, unknown>>; columns: Array<string> };

export type LensCardViewWhereEntry = NonNullable<LensCardView['where']>[number];

/**
 * Keeps or drops a row per the view's declared filters, before any grouping:
 * a card reading a narrower cut of a shared dataset (one country, one
 * product) is this, not a second, near-identical dataset. A row passes one
 * entry when its stringified value for the column is in `in` when given and
 * not in `notIn` when given; every entry has to pass for the row to survive.
 */
function applyWhere(
  rows: Array<Record<string, unknown>>,
  where: LensCardView['where'],
): Array<Record<string, unknown>> {
  if (!where?.length) {
    return rows;
  }
  return rows.filter((row) =>
    where.every((entry) => {
      const value = String(row[entry.column] ?? '');
      if (entry.in && !entry.in.includes(value)) {
        return false;
      }
      if (entry.notIn && entry.notIn.includes(value)) {
        return false;
      }
      return true;
    }),
  );
}

/**
 * The measures a view names that the result does not carry and no aggregate
 * field computes. A card keeps its view when the query behind it is rewritten,
 * so this is how a card comes to ask for a column that stopped existing. The
 * shaping below reduces that to one null, which reads as "no rows" when the
 * fault is the card's field choice.
 *
 * A ratio's own name is not a column (it is computed from `of` and `per`), so
 * for a ratio view only those two inputs have to exist.
 */
export function missingViewMeasures(
  view: LensCardView | undefined,
  columns: ReadonlyArray<string>,
  aggregateFields: ReadonlyArray<ReadyField> = [],
  datasetColumns: ReadonlyArray<LensDatasetColumn> = [],
  type?: LensCardType,
): Array<string> {
  if (!view) {
    return [];
  }
  const named = view.ratio ? [view.ratio.of, view.ratio.per] : tableMeasureInputs(view, type, datasetColumns);
  const computed = new Set(aggregateFields.map((entry) => entry.field.name));
  return named.filter(
    (column, index, all): column is string =>
      Boolean(column) && all.indexOf(column) === index && !columns.includes(column) && !computed.has(column),
  );
}

/**
 * Reduces a dataset's rows to the shape one card draws. A shared dataset
 * returns a single row set, so the cut has to happen here rather than in SQL:
 * that is what lets a metric and a stacked chart read the same query.
 */
export function applyView(
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  view: LensCardView | undefined,
  type: LensCardType,
  /**
   * The dataset's aggregate calculated fields. Evaluated after grouping, since
   * a column reference inside one means that column's total for the group;
   * row-level fields are applied before this is reached, because a reader
   * filters and groups by them like any other column.
   */
  aggregateFields: ReadonlyArray<ReadyField> = [],
  /**
   * True when the backend already grouped these rows (a governed dataset's card compiles to one
   * aggregate query, LensCardData.aggregated). One row per group means every reduce below has to
   * be a sum regardless of what the view's own Combine picked: countDistinct, avg, min, max and
   * first are all wrong a second time over rows that are already somebody's total. A ratio is
   * unaffected, since it always sums its two parts rather than reading `aggregate`.
   */
  aggregated = false,
  datasetColumns: ReadonlyArray<LensDatasetColumn> = [],
): Shaped {
  if (!view || !hasCut(view)) {
    return { rows, columns };
  }

  // A governed card's backend query has already applied its saved view.where
  // before returning one aggregate row per group. Applying it again here
  // would discard those rows when the query correctly omitted the filter-only
  // field (for example, a sale-side filter behind a returned order count).
  // Workspace rows still need the client-side cut before their metric/group
  // reduction below.
  rows = aggregated ? rows : applyWhere(rows, view.where);

  // A coarser time bucket is a column the dataset does not return. Deriving it
  // here is what lets one daily dataset serve a daily card and a monthly one:
  // the alternative is a second query per grain, which is what a reader was
  // asking AskRichard to write.
  const grained = withGrainColumn(rows, columns, view.x);
  rows = grained.rows;
  columns = grained.columns;

  // Measures in plot order: value first, then any others named.
  const listed = [view.value, ...(view.values ?? [])].filter(
    (column, index, all): column is string => Boolean(column) && all.indexOf(column) === index,
  );
  const tableRatios = namedTableRatios(view, type, datasetColumns);
  const ratioNames = new Set(tableRatios.map((column) => column.name));
  const metadata = new Map(datasetColumns.map((column) => [column.name, column]));
  const columnKeys =
    type === 'table' && !view.x && !view.series
      ? listed.filter((column) => {
          if (ratioNames.has(column)) return false;
          const kind = tableRatios.length ? metadata.get(column)?.kind : undefined;
          return kind ? kind === 'date' || kind === 'dimension' : !isMeasureColumn(rows, column);
        })
      : [];
  const named = listed.filter((column) => !columnKeys.includes(column));
  // A view may group without naming a measure, so fall back to the first
  // numeric column. Leaving measures empty drops the number being plotted and
  // the card renders nothing but its keys.
  const inferred = view.ratio
    ? undefined
    : columns.find((column) => column !== view.x && column !== view.series && isMeasureColumn(rows, column));
  const measures = named.length ? named : inferred ? [inferred] : [];
  const value = measures[0];
  const aggregate = aggregated ? 'sum' : (view.aggregate ?? 'sum');
  const ratio = view.ratio;
  // A ratio is computed from the group's totals, so its result is not one of the dataset's
  // columns and can only carry a name the view declared. `value` falls back to the inferred
  // numeric column above when the view names none, and naming a ratio after that column would
  // label and format it as an unrelated measure.
  const ratioName = named[0] ?? ratio?.of;
  // A ratio may deliberately have no `value`: the governed measure lives in
  // `view.ratio`, and its numerator becomes the stable output key. The rows
  // below already carry that computed key; the returned column list must carry
  // it too or the chart receives numeric rows with no numeric series and says
  // it cannot draw them.
  const outputMeasures = ratio && ratioName ? [ratioName] : measures;
  // An aggregate field is not a column any row holds: it is computed from the
  // group's totals. Those totals have to survive grouping for it to be computed
  // at all, whether or not the card names them as measures, and a field reading
  // another field pulls that one's inputs in too. A ratio is the same shape of
  // thing, spelled shorter.
  const fieldsInPlay = dependencyClosure(aggregateFields, [
    ...measures,
    ...tableRatios.flatMap((column) => [column.ratio.of, column.ratio.per]),
  ]);
  const computed = new Set(fieldsInPlay.map((entry) => entry.field.name));
  const carried = [
    ...new Set([
      ...measures.filter((measure) => !computed.has(measure) && !ratioNames.has(measure)),
      ...tableRatios.flatMap((column) => [column.ratio.of, column.ratio.per]),
      ...(ratio ? [ratio.of, ratio.per] : []),
      ...aggregateFieldInputs(fieldsInPlay),
    ]),
  ];

  const reduceTableColumn = (column: string, values: Array<unknown>) =>
    reduce(values, !aggregated && tableRatios.length ? (metadata.get(column)?.aggregate ?? aggregate) : aggregate);
  const withTableRatios = (totals: Record<string, unknown>) => ({
    ...totals,
    ...Object.fromEntries(
      tableRatios.map((column) => {
        const of = totals[column.ratio.of];
        const per = totals[column.ratio.per];
        return [column.name, isNumeric(of) && isNumeric(per) && Number(per) !== 0 ? Number(of) / Number(per) : null];
      }),
    ),
  });

  // A scatter or a box plot is points by definition: a box plot's quantiles
  // are computed from the individual rows in each category, so pre-grouping
  // would leave it nothing to compute them from. A heatmap does the opposite:
  // one row per (x, y) pair is exactly the cell it draws, so it keeps the
  // 'grouped' default below.
  const mode = view.mode ?? (type === 'scatter' || type === 'boxplot' ? 'points' : 'grouped');

  // One mark per row: a form plotting two measures against each other is
  // describing individual rows, so grouping them away would erase the point.
  if (mode === 'points') {
    const kept = [view.x, view.series, ...measures].filter(
      (column, index, all): column is string => Boolean(column) && all.indexOf(column) === index,
    );
    if (!kept.length) {
      return { rows, columns };
    }
    return {
      columns: kept,
      rows: rows.map((row) => {
        const evaluated = withTableRatios(row);
        return Object.fromEntries(kept.map((column) => [column, evaluated[column]]));
      }),
    };
  }

  // A metric is one number, so every row collapses into it.
  if (type === 'metric') {
    if (ratio) {
      const name = ratioName ?? ratio.of;
      return { columns: [name], rows: [{ [name]: divide(rows, ratio) }] };
    }
    if (!value) {
      return { rows, columns };
    }
    if (computed.has(value)) {
      // The one collapsed row holds every total the field reads, which is what
      // makes a rate here the rate over the whole period rather than a mean of
      // daily rates.
      const totals = Object.fromEntries(
        carried.map((column) => [
          column,
          reduce(
            rows.map((row) => row[column]),
            aggregate,
          ),
        ]),
      );
      const [only] = applyAggregateFields([totals], fieldsInPlay);
      return { columns: [value], rows: [{ [value]: only[value] ?? null }] };
    }
    return {
      columns: [value],
      rows: [
        {
          [value]: reduce(
            rows.map((row) => row[value]),
            aggregate,
          ),
        },
      ],
    };
  }

  const keys = [view.x, view.series, ...columnKeys].filter(Boolean);
  if ((!keys.length && !tableRatios.length) || (!value && !ratio)) {
    return { rows, columns };
  }

  const groups = new Map<string, { key: Record<string, unknown>; values: Record<string, Array<unknown>> }>();
  for (const row of rows) {
    // A plain join(' ') lets two different key tuples collide whenever a value
    // itself contains a space, for example ['foo bar', 'baz'] and
    // ['foo', 'bar baz'] both joining to "foo bar baz". JSON.stringify quotes
    // and escapes each value, so the encoding is unambiguous.
    const signature = JSON.stringify(keys.map((column) => String(row[column] ?? '')));
    const existing = groups.get(signature);
    if (existing) {
      carried.forEach((measure) => existing.values[measure].push(row[measure]));
      continue;
    }
    groups.set(signature, {
      key: Object.fromEntries(keys.map((column) => [column, row[column]])),
      values: Object.fromEntries(carried.map((measure) => [measure, [row[measure]]])),
    });
  }

  let grouped = [...groups.values()].map((group) => {
    if (ratio) {
      const name = ratioName ?? ratio.of;
      return {
        ...group.key,
        [name]: ratioOf(group.values[ratio.of] ?? [], group.values[ratio.per] ?? []),
        [LENS_RATIO_PARTS]: {
          numerator: Number(reduce(group.values[ratio.of] ?? [], 'sum') ?? 0),
          denominator: Number(reduce(group.values[ratio.per] ?? [], 'sum') ?? 0),
        },
      };
    }
    const totals = {
      ...group.key,
      // Named table ratios use governed operand/measure reducers on raw rows;
      // compiled aggregate helpers sum when the same final groups are reshaped.
      ...Object.fromEntries(carried.map((column) => [column, reduceTableColumn(column, group.values[column])])),
    };
    if (!fieldsInPlay.length && !tableRatios.length) {
      return totals;
    }
    const [withFields] = applyAggregateFields([totals], fieldsInPlay);
    const evaluated = withTableRatios(withFields);
    // Only what the card asked for: the totals a field was computed from are
    // not measures this card plots, and left in they would each become a series.
    return Object.fromEntries([...keys, ...measures].map((column) => [column, evaluated[column] ?? null]));
  });

  // A limit keeps the largest groups, which is what "top N" means to a reader.
  if (view.limit && grouped.length > view.limit) {
    const totals = new Map<string, number>();
    for (const row of grouped) {
      const outer = String(row[keys[0]] ?? '');
      totals.set(outer, (totals.get(outer) ?? 0) + Math.abs(Number(row[outputMeasures[0]]) || 0));
    }
    const kept = new Set(
      [...totals.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, view.limit)
        .map(([name]) => name),
    );
    grouped = grouped.filter((row) => kept.has(String(row[keys[0]] ?? '')));
  }

  return { rows: grouped, columns: [...keys, ...outputMeasures] };
}

/**
 * Adds the bucket column a grained x names, leaving every other column alone.
 *
 * The bucket is the first day of its period, so it stays a date: the axis keeps
 * reading it as time rather than dropping to a row of category labels.
 */
function withGrainColumn(rows: Array<Record<string, unknown>>, columns: Array<string>, x: string | undefined): Shaped {
  const grain = parseGrain(x);
  if (!grain || !columns.includes(grain.column)) {
    return { rows, columns };
  }
  return {
    columns: [...columns, x],
    rows: rows.map((row) => ({ ...row, [x]: bucketOf(row[grain.column], grain.grain, undefined, grain.column) })),
  };
}

function hasCut(view: LensCardView): boolean {
  return Boolean(
    view.x ||
    view.series ||
    view.value ||
    view.values?.length ||
    view.aggregate ||
    view.limit ||
    view.mode ||
    view.ratio ||
    view.where?.length,
  );
}

/**
 * The aggregate fields a card actually needs, in the order they must be
 * evaluated: the ones it names as measures, plus every field those depend on.
 */
function dependencyClosure(fields: ReadonlyArray<ReadyField>, measures: ReadonlyArray<string>): Array<ReadyField> {
  const byName = new Map(fields.map((entry) => [entry.field.name, entry]));
  const needed = new Set<string>();
  const queue = measures.filter((measure) => byName.has(measure));

  while (queue.length) {
    const name = queue.shift();
    if (needed.has(name)) {
      continue;
    }
    needed.add(name);
    const entry = byName.get(name);
    entry?.reads.filter((column) => byName.has(column)).forEach((column) => queue.push(column));
  }

  // `fields` arrives already ordered so a field follows what it reads, so
  // filtering it preserves an order the evaluator can run.
  return fields.filter((entry) => needed.has(entry.field.name));
}

/**
 * A ratio of two sums, which is the only correct way to roll up a rate: the
 * overall margin rate is total margin over total TTV, never the average of the
 * per-category rates, which weights a category with three bookings the same as
 * one with thirty thousand.
 */
function ratioOf(of: Array<unknown>, per: Array<unknown>): number | null {
  const total = (values: Array<unknown>): number =>
    values
      .filter((entry) => isNumeric(entry))
      .map((entry) => Number(entry))
      .reduce((sum, entry) => sum + entry, 0);
  const denominator = total(per);
  return denominator === 0 ? null : total(of) / denominator;
}

/** The same ratio taken across every row, for a metric. */
function divide(rows: Array<Record<string, unknown>>, ratio: { of: string; per: string }): number | null {
  return ratioOf(
    rows.map((row) => row[ratio.of]),
    rows.map((row) => row[ratio.per]),
  );
}

function reduce(values: Array<unknown>, aggregate: LensAggregate): number | unknown {
  if (aggregate === 'count') {
    return values.length;
  }
  if (aggregate === 'first') {
    return values[0];
  }

  if (aggregate === 'countDistinct') {
    // Matches SQL COUNT(DISTINCT x): blanks are not a value, so they do not
    // count as one, and 1 and '1' are the same identifier from BigQuery.
    const seen = new Set(
      values.filter((value) => value !== null && value !== undefined && value !== '').map((value) => String(value)),
    );
    return seen.size;
  }

  const numbers = values.filter((value) => isNumeric(value)).map((value) => Number(value));
  if (!numbers.length) {
    return null;
  }

  switch (aggregate) {
    case 'avg':
      return numbers.reduce((total, value) => total + value, 0) / numbers.length;
    case 'min':
      return Math.min(...numbers);
    case 'max':
      return Math.max(...numbers);
    default:
      return numbers.reduce((total, value) => total + value, 0);
  }
}

/**
 * Chosen values per column. An absent column is not narrowing anything, which
 * is what everything ticked amounts to; an empty list is every value unticked
 * and shows nothing, which is a reader saying so rather than an oversight.
 */
export type LensSelection = Record<string, Array<string>>;

export type LensMarkPick = { column: string; value: string; replaceValues?: Array<string> };

/**
 * Cross-filtering is only honest within a dataset: cards sharing a query share
 * a row set, so narrowing it needs no query and cannot disagree with itself.
 * Columns combine with AND, values within a column with OR, which is what a
 * reader means by picking two stores and one month.
 */
export function applySelection(
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  selection: LensSelection | undefined,
): Array<Record<string, unknown>> {
  const active = Object.entries(selection ?? {}).filter(
    // A dataset that does not carry the column cannot answer that part of the
    // selection, so it is ignored rather than filtering the card to nothing.
    ([column, values]) => Array.isArray(values) && columns.includes(column),
  );
  if (!active.length) {
    return rows;
  }

  return rows.filter((row) => active.every(([column, values]) => values.includes(String(row[column] ?? ''))));
}

/** Distinct values of a column, for a selection dropdown. */
export function distinctValues(rows: Array<Record<string, unknown>>, column: string, cap = 200): Array<string> {
  const seen = new Set<string>();
  for (const row of rows) {
    const value = row[column];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    seen.add(String(value));
    if (seen.size >= cap) {
      break;
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/** Adds or removes one value, which is what clicking a mark means. */
export function toggleSelection(
  selection: LensSelection,
  column: string,
  value: string,
  /** Every value the column offers, so a full tick list can collapse to "all". */
  options?: Array<string>,
): LensSelection {
  // No entry means everything, so the first click on a mark narrows to that
  // one value rather than starting from nothing.
  const current = selection[column];
  const next =
    current === undefined
      ? [value]
      : current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value];

  const updated = { ...selection };
  // Ticking every value is the same as no filter, and storing it that way
  // keeps one canonical shape for "all".
  if (next.length === options?.length) {
    delete updated[column];
  } else {
    updated[column] = next;
  }
  return updated;
}

/**
 * Clicking a mark is a two-state gesture: the same mark turns its own filter on
 * and then off again. Emptying a column this way means no filter at all, unlike
 * the checklist control, where unticking every value deliberately means show
 * nothing. Without this the second click leaves an empty list behind and the
 * card renders zero rows instead of returning to normal.
 */
export function toggleMarkSelection(selection: LensSelection, column: string, value: string): LensSelection {
  const updated = toggleSelection(selection, column, value);
  if (updated[column]?.length === 0) {
    const cleared = { ...updated };
    delete cleared[column];
    return cleared;
  }
  return updated;
}

export function hasSelection(selection: LensSelection | undefined): boolean {
  // A present column narrows, empty included: nothing ticked shows nothing.
  return Object.keys(selection ?? {}).length > 0;
}

/** A date reads as text but belongs in a calendar control, not a value list. */
export function looksLikeDate(rows: Array<Record<string, unknown>>, column: string): boolean {
  const sample = rows
    .slice(0, 20)
    .map((row) => row[column])
    .filter((value) => value !== null && value !== '');
  if (!sample.length) {
    return false;
  }
  return sample.every((value) => isDateLikeText(value));
}

/** Numeric columns are measures: filtered by range rather than by value. */
export function numericColumns(rows: Array<Record<string, unknown>>, columns: Array<string>): Array<string> {
  return columns.filter((column) => isMeasureColumn(rows, column));
}

export type LensRange = { min?: number; max?: number };

/** Ranges narrow a measure, and combine with the value selections. */
export function applyRanges(
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  ranges: Record<string, LensRange> | undefined,
): Array<Record<string, unknown>> {
  const active = Object.entries(ranges ?? {}).filter(
    ([column, range]) => columns.includes(column) && (range.min !== undefined || range.max !== undefined),
  );
  if (!active.length) {
    return rows;
  }

  return rows.filter((row) =>
    active.every(([column, range]) => {
      if (!isNumeric(row[column])) {
        return false;
      }
      const value = Number(row[column]);
      if (range.min !== undefined && value < range.min) {
        return false;
      }
      if (range.max !== undefined && value > range.max) {
        return false;
      }
      return true;
    }),
  );
}

/** The span a measure covers, for the range control's placeholders. */
export function valueExtent(rows: Array<Record<string, unknown>>, column: string): LensRange {
  const numbers = rows
    .map((row) => row[column])
    .filter(isNumeric)
    .map(Number);
  return numbers.length ? { min: Math.min(...numbers), max: Math.max(...numbers) } : {};
}

const MAX_INFERRED_DIMENSIONS = 4;
const MAX_DIMENSION_VALUES = 50;

/**
 * Works out which columns a reader could filter by, for a dataset that never
 * declared any. A dimension repeats and is not a measure: a column of distinct
 * numbers is something to sum, and one with a value per row is an identifier,
 * neither of which makes a usable dropdown.
 */
export function inferDimensions(rows: Array<Record<string, unknown>>, columns: Array<string>): Array<string> {
  if (!rows.length) {
    return [];
  }

  const candidates = columns
    // Dates belong in a calendar control, and numbers in a range one.
    .filter((column) => !isMeasureColumn(rows, column) && !looksLikeDate(rows, column))
    .map((column) => ({ column, values: distinctValues(rows, column, MAX_DIMENSION_VALUES + 1).length }))
    .filter(({ values }) => {
      if (values <= 1 || values > MAX_DIMENSION_VALUES) {
        return false;
      }
      // Nearly one value per row is an identifier, not a dimension. Judged by
      // ratio rather than count, so a small result is not mistaken for one.
      return !(rows.length >= 10 && values / rows.length > 0.8);
    });

  // Fewest values first: the coarsest split is the one worth offering.
  return candidates
    .sort((a, b) => a.values - b.values)
    .slice(0, MAX_INFERRED_DIMENSIONS)
    .map(({ column }) => column);
}

/** The dashboard period. Applied by BigQuery, not by filtering loaded rows:
 *  a different period is a different question, not a narrowing of this one. */
export type LensDateRange = { from?: string; to?: string };

/** The date column a dataset is keyed on, if it has one. */
export function dateColumn(rows: Array<Record<string, unknown>>, columns: Array<string>): string | undefined {
  return columns.find((column) => looksLikeDate(rows, column));
}

const MAX_SLICES = 8;

export type PieSlice = { name: string; value: number; share: number };

/**
 * Slices for a pie: one category, one measure, largest first. A pie encodes
 * magnitude by angle, which people read poorly past a handful of slices, so
 * the tail is folded rather than drawn as a fan of slivers.
 */
export function buildPieModel(
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  view: LensCardView | undefined,
): { slices: Array<PieSlice>; folded: number } | null {
  if (!rows.length) {
    return null;
  }

  const nameKey = view?.x ?? view?.series ?? columns.find((column) => !isMeasureColumn(rows, column));
  const valueKey = view?.value ?? columns.find((column) => column !== nameKey && isMeasureColumn(rows, column));
  if (!nameKey || !valueKey) {
    return null;
  }

  const totals = new Map<string, number>();
  for (const row of rows) {
    const name = String(row[nameKey] ?? '(null)');
    const value = isNumeric(row[valueKey]) ? Number(row[valueKey]) : 0;
    totals.set(name, (totals.get(name) ?? 0) + value);
  }

  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]);
  const kept = ranked.slice(0, ranked.length > MAX_SLICES ? MAX_SLICES - 1 : MAX_SLICES);
  const tail = ranked.slice(kept.length);
  const entries = tail.length
    ? [...kept, [OTHER_SERIES, tail.reduce((sum, [, value]) => sum + value, 0)] as [string, number]]
    : kept;

  // A share of nothing is not a share, so a total of zero has no pie.
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  if (!total) {
    return null;
  }

  // A pie encodes magnitude by angle, and an angle cannot be negative. Dividing by a signed total
  // reads as a share while being none: a category that nets negative, which margin and refund
  // data do routinely, drives the other slices past 100% and itself below zero. There is no
  // honest pie of mixed signs, so this declines to draw one and the card falls to its empty
  // state, the same way a zero total does above.
  if (entries.some(([, value]) => value < 0)) {
    return null;
  }

  return {
    slices: entries.map(([name, value]) => ({ name, value, share: value / total })),
    folded: tail.length,
  };
}
